import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import type { Period } from 'lumen/models';
import { hasOTBDefaultValue } from 'lumen/utils/models/occupancy';

interface HotelPricingAssistantTableCellsTdHotelPickUpMonthSignature {
  Args: {
    value: Period;
  };
}

/* Rooms booked now minus rooms booked on the extract the week view's OTB change
   compares against, so every change shows, even below its 0.5 pp threshold. */
export default class HotelPricingAssistantTableCellsTdHotelPickUpMonth extends Component<HotelPricingAssistantTableCellsTdHotelPickUpMonthSignature> {
  @service('hotel-and-market')
  declare hotelAndMarketService: Services['hotel-and-market'];
  @service declare intl: Services['intl'];
  @service('pricing-assistant-data-request-service')
  declare pricingAssistantDataRequestService: Services['pricing-assistant-data-request-service'];

  get displayValue(): string {
    return this.isMissing
      ? this.intl.t('general.missing')
      : this.intl.formatNumber(this.pickUp, { signDisplay: 'exceptZero' });
  }

  /* The request task also fetches holidays and events, so the pulse can outlast occupancy. */
  get isLoading(): boolean {
    return this.pricingAssistantDataRequestService
      .pricingAssistantGeneralDataRequestsTask.isRunning;
  }

  get isMissing(): boolean {
    return this.pickUp === null;
  }

  get isPast(): boolean {
    return this.hotelAndMarketService.isPast(this.args.value.fromDate);
  }

  /* `roomsBookedChangeVersus` doesn't check the OTB placeholder, so the cell does.
     Past dates have no pick-up, as the week view shows no change for them. */
  get pickUp(): number | null {
    const occupancy = this.args.value.day.occupancy;
    const referenceOccupancy = this.args.value.referenceOccupancy;

    if (
      this.isPast ||
      !occupancy ||
      !referenceOccupancy ||
      hasOTBDefaultValue(occupancy.roomsBooked) ||
      hasOTBDefaultValue(referenceOccupancy.rooms_booked)
    ) {
      return null;
    }

    return occupancy.roomsBookedChangeVersus(referenceOccupancy);
  }

  <template>
    {{#if this.isLoading}}
      <div
        class="loading-pulse"
        data-test-id="td-hotel-pick-up-month-loading"
      ></div>
    {{else}}
      <div
        class="mr-500 flex items-center justify-end
          {{if
            this.isPast
            'text-disabled'
            (if this.isMissing 'text-neutral-subdued')
          }}"
        data-test-id="td-hotel-pick-up-month"
      >
        <span data-test-id="td-hotel-pick-up-month-value">
          {{this.displayValue}}
        </span>
      </div>
    {{/if}}
  </template>
}
